/**
 * Feature toggle registry for SpectrumReach.com.
 *
 * Every toggle name is declared here, and only here.
 *
 * Naming convention: `DomainPageFeature` in PascalCase (e.g. `HomeHeroAutoplay`),
 * optionally preceded by a prefix that changes how the toggle behaves:
 *
 * - `Config_` marks a Config toggle. Its value comes only from the environment
 *   config: it cannot be overridden from sessionStorage or by an external
 *   toggle source.
 * - `Mock` marks a test-only toggle. It exists for tests and mock data and must
 *   not drive production behavior.
 *
 * IMPORTANT: every member's key MUST equal its string value
 * (`Foo = 'Foo'`). Multi-toggle lookups key their results by the enum value, so
 * a mismatch silently breaks them. `featureToggles.types.test.ts` enforces this.
 *
 * Permanent toggles (ones that must survive flag cleanup) must say so in a
 * comment on the member: `Permanent: <reason>`.
 */
export enum FeatureToggleName {
    // --- Release toggles -----------------------------------------------------

    /**
     * Placeholder pilot toggle. Replace with the real pilot flag once
     * SRWEB-2062 OQ-3 (which feature goes first) is answered.
     */
    ExamplePilotFeature = `ExamplePilotFeature`,

    // --- Config toggles (`Config_` prefix, env config only) -----------------

    /** Example Config toggle: shows how `Config_` toggles are declared. */
    Config_ExampleConfigToggle = `Config_ExampleConfigToggle`,

    /**
     * Permanent: builds the toggle check page at `/feature-flag-check/`
     * (`src/pages/feature-flag-check.tsx`) through `pageGates`. Off in
     * production, so the page is never built there.
     */
    Config_FeatureFlagCheckPage = `Config_FeatureFlagCheckPage`,

    /**
     * Permanent: switches the runtime toggle source on or off per environment.
     * When on, the provider loads `/feature-toggles.json` after mount and
     * merges its values over the env config (see `featureToggles.runtime.ts`).
     * Off by default so the site runs on env config alone.
     */
    Config_RuntimeToggleSource = `Config_RuntimeToggleSource`,

    // --- Test-only toggles (`Mock` prefix) -----------------------------------

    /** Example test-only toggle, used by feature toggle tests. */
    MockExampleScenario = `MockExampleScenario`,
}

/** Prefix that marks a Config toggle (env config only, never overridden). */
export const CONFIG_TOGGLE_PREFIX = `Config_`;

/** Prefix that marks a test-only toggle. */
export const MOCK_TOGGLE_PREFIX = `Mock`;

/** One toggle and its value. */
export interface IFeatureToggle {
    readonly name: FeatureToggleName;
    readonly isEnabled: boolean;
}

/** An environment's full set of toggle values. */
export interface IFeatureToggles {
    readonly environment: string;
    readonly toggles: IFeatureToggle[];
}

/** Toggle values keyed by toggle name. */
export type IFlatFeatureToggles = { [key in FeatureToggleName]: boolean };
